import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@spartacus/core';
import { FocusConfig, LaunchDialogService } from '@spartacus/storefront';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { ArticlePickerService } from '../../../../core/catalog';
import { BaseAbstractModalComponent, ModalCloseReason } from '../../../../core/modal';
import {
  Article,
  ArticlePickerAttribute,
  ArticlePickerEvent,
  CartType,
  EntityMap,
  Product,
  SolrResultEntityRef,
} from '../../../../core/model';
import { shallowEqualArrays, shallowEqualObjects } from '../../../../core/util';
import { CheckoutFacade } from '../../../checkout/base';
import { ItemListType } from '../../../tracking';

interface Pagination {
  currentScrollIndex: number;
  itemsPerPage: number;
  totalItems: number;
}

export interface PdpListVariantsModalData {
  areaOfUseCode: string;
  product: Product;
  selectedAttributes?: ArticlePickerEvent[];
}

@Component({
  selector: 'py-pdp-list-variants-modal',
  templateUrl: './pdp-list-variants-modal.component.html',
  styleUrls: ['./pdp-list-variants-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PdpListVariantsModalComponent extends BaseAbstractModalComponent implements OnInit, OnDestroy {
  readonly stockCartType = CartType.stock;
  private subscriptions = new Subscription();
  focusConfig: FocusConfig = {
    trap: true,
    block: true,
    autofocus: false,
  };

  areaOfUseCode: string;
  product: Product;
  autoSelectedAttributes: string[] = [];
  pagination: Pagination = {
    currentScrollIndex: 9,
    itemsPerPage: 9,
    totalItems: 0,
  };

  selectedAttributes$ = new BehaviorSubject<ArticlePickerEvent[]>([]);

  set selectedAttributes(selectedAttributes: ArticlePickerEvent[]) {
    this.selectedAttributes$.next(selectedAttributes);
  }

  articles$: Observable<Article[]>;
  filteredArticleRefs$: Observable<SolrResultEntityRef[]> = of([]);
  attributes$: Observable<ArticlePickerAttribute[]>;
  isUserLoggedIn$: Observable<boolean>;
  isCheckoutEnabled$: Observable<boolean>;
  articleItemQueryParams$: Observable<EntityMap<string>>;

  ltemListTypePDP = ItemListType.PDP_ARTICLES_LIST;

  constructor(
    private articlePickerService: ArticlePickerService,
    private activatedRoute: ActivatedRoute,
    private checkoutService: CheckoutFacade,
    private authService: AuthService,
    protected launchDialogService: LaunchDialogService,
    protected el: ElementRef,
    protected renderer: Renderer2
  ) {
    super(el, launchDialogService, renderer);
    this.handlerForDismissOnClickOutside = this.dismissModal;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.launchDialogService.data$.pipe(take(1)).subscribe((data: PdpListVariantsModalData) => {
        this.areaOfUseCode = data.areaOfUseCode;
        this.product = data.product;

        if (data.selectedAttributes) {
          this.selectedAttributes = data.selectedAttributes;
        }

        this.attributes$ = this.articlePickerService.getAttributes(this.product, this.areaOfUseCode);
        this.articles$ = this.articlePickerService.getArticles(this.product, this.areaOfUseCode);
      })
    );

    this.isCheckoutEnabled$ = this.checkoutService.isCheckoutEnabled(this.stockCartType);
    this.isUserLoggedIn$ = this.authService.isUserLoggedIn();

    this.filteredArticleRefs$ = this.selectedAttributes$.pipe(
      distinctUntilChanged(shallowEqualArrays),
      withLatestFrom(this.articles$),
      map(([selectedAttributes, articles]) => this.articlePickerService.getFilteredArticles(articles, selectedAttributes)),
      map((articles) => articles?.map((article) => ({ ref: article?.code, score: 0, ts: '' })) || []),
      tap((value) => {
        this.pagination.currentScrollIndex = this.pagination.itemsPerPage;
        this.pagination.totalItems = value?.length || 0;
      })
    );

    this.articleItemQueryParams$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('mya')),
      map((myaValue) => (!!myaValue ? { mya: myaValue } : {}))
    );
  }

  updateSelectedAttribute(event: ArticlePickerEvent): void {
    const oldAttribute = this.selectedAttributes$.value.find((sa) => sa.attributeCode === event.attributeCode);

    if (!shallowEqualObjects(oldAttribute, event)) {
      const newAttributes = [...this.selectedAttributes$.value.filter((sa) => sa.attributeCode !== event.attributeCode)];

      if (!!event.value) {
        newAttributes.push(event);
      }

      this.selectedAttributes$.next(newAttributes);
    }
  }

  reset(): void {
    this.selectedAttributes$.next([]);
  }

  dismissModal(reason?: any): void {
    this.launchDialogService.closeDialog(reason ?? ModalCloseReason.PDP_LIST_VARIANTS_DISMISSED);
  }

  nextScrollIndex(): void {
    if (this.pagination.currentScrollIndex >= this.pagination.totalItems || this.pagination.totalItems === 0) {
      return;
    }

    if (this.pagination.currentScrollIndex + this.pagination.itemsPerPage < this.pagination.totalItems) {
      this.pagination.currentScrollIndex = this.pagination.currentScrollIndex + this.pagination.itemsPerPage;
    } else {
      this.pagination.currentScrollIndex = this.pagination.totalItems;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
